import { TaxPaymentScheduleQuarter } from "@wingspanhq/wingspan-api-sdk";
import {
  toWSDateString,
  WSActions,
  WSAlert,
  WSCard,
  WSContainer,
  WSElement,
  WSInfoBox,
  WSInputNumber,
  WSList,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { getApiErrorMessage } from "../../../../v3/utils/apiError";
import {
  getCurrentTaxPaymentQuarter,
  Quarter
} from "@wingspanhq/utils/dist/date/taxDeadlines";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesInternalAccounts, queriesTaxWithholdings } from "@query";
import { useUserId } from "../../../../query/hooks/helpers";
import { IdempotencyKey } from "@utils/idempotencyKey";
import { selectorAccountBalance } from "../../../../shared/selectors/selectorAccountBalance";
import { WSQueries } from "../../../../query/WSQuery";
import {
  useQueryActivities,
  useQueryMemberProfile
} from "../../../../query/users/queries";
import { FromTo } from "../../../../shared/components/FromTo";
import { selectorTaxWithholdingAccount } from "../../../../shared/selectors/selectorTaxWithholdingAccount";
import { LabelMethod } from "../../../Settings/components/LabelMethod";
import {
  PATH_TAX_WITHHOLDINGS,
  routeHelpersTaxWithholdingsSchedulePaymentConfirmation
} from "../../paths";
import { useModalAddSSN } from "./ModalAddSSN";
import { useModalConsentEFTPS } from "./ModalConsentEFTPS";
import { Layout } from "../../../../shared/components/Layout";
import { formatInternalDeadline } from "../../utils";

const quarterInfo = getCurrentTaxPaymentQuarter();

const quarterToScheduleQuarter: Record<Quarter, TaxPaymentScheduleQuarter> = {
  [Quarter.Q1]: TaxPaymentScheduleQuarter.Q1,
  [Quarter.Q2]: TaxPaymentScheduleQuarter.Q2,
  [Quarter.Q3]: TaxPaymentScheduleQuarter.Q3,
  [Quarter.Q4]: TaxPaymentScheduleQuarter.Q4
};

type SchedulePaymentErrorProps = {
  error?: unknown;
  internalDeadline: Date;
  quarter: Quarter;
  year: number;
};

export const SchedulePaymentError: React.FC<SchedulePaymentErrorProps> = ({
  error,
  internalDeadline,
  quarter,
  year
}) => {
  const isDuplicatePaymentError =
    !!error &&
    /pending tax payment already exists/i.test(getApiErrorMessage(error) || "");

  if (!isDuplicatePaymentError) {
    return <ErrorAlert context="PendingWithholding" error={error} />;
  }

  return (
    <WSAlert
      theme="error"
      icon="info-circle"
      title={`Unable to schedule the payment. A pending tax payment already exists for ${quarter} ${year}.`}
      data-testid="error-alert"
    >
      <WSList gap="S">
        <WSText.ParagraphSm weight="medium">
          Need to make changes?
        </WSText.ParagraphSm>
        <WSText.ParagraphSm>
          You can cancel and reschedule anytime before this quarter&apos;s
          processing deadline: {formatInternalDeadline(internalDeadline)}. After
          this date your payment will be locked for processing and cannot be
          cancelled.
        </WSText.ParagraphSm>
      </WSList>
    </WSAlert>
  );
};

export const FormSchedulePayment: React.FC = () => {
  const history = useHistory();
  const userId = useUserId();

  const goToConfirmation =
    routeHelpersTaxWithholdingsSchedulePaymentConfirmation.useGoTo();

  const modalAddSSN = useModalAddSSN();
  const modalConsentEFTPS = useModalConsentEFTPS();

  const queryMemberProfile = useQueryMemberProfile(userId);
  const queryActivity = useQueryActivities(userId);
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const taxWithholdingAccountId = queryInternalAccounts.data?.data?.find(
    selectorTaxWithholdingAccount
  )?.id;
  const queryWithholdingBalance =
    queriesInternalAccounts.getInternalAccountBalance.useQuery(
      { internalAccountId: taxWithholdingAccountId! },
      { enabled: !!taxWithholdingAccountId }
    );

  const [scheduleTaxPayment, scheduleTaxPaymentMeta] =
    queriesTaxWithholdings.scheduleTaxPayment.useMutation();

  const [amount, setAmount] = useState<number | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!amount) return;

    if (!queryMemberProfile.data?.profile.ssnProvided) {
      const ssnResult = await modalAddSSN.open();
      if (!ssnResult) return;
    }

    if (!queryActivity.data?.events.eftpsEnrolledAt) {
      const eftpsResult = await modalConsentEFTPS.open();
      if (!eftpsResult) return;
    }

    const idempotencyKey = new IdempotencyKey({
      amount,
      quarter: quarterInfo.quarter,
      year: quarterInfo.year
    });

    await scheduleTaxPayment(
      {
        idempotencyKey: idempotencyKey.value,
        taxPaymentSchedule: {
          amount,
          quarter: quarterToScheduleQuarter[quarterInfo.quarter],
          year: quarterInfo.year
        }
      },
      {
        onSuccess: result => {
          idempotencyKey.destroy();
          goToConfirmation(
            { taxPaymentId: result.id },
            { state: { amount: Number(result.amount) } }
          );
        }
      }
    );
  }, [
    scheduleTaxPayment,
    modalAddSSN,
    modalConsentEFTPS,
    goToConfirmation,
    queryActivity.data,
    queryMemberProfile.data,
    amount
  ]);

  return (
    <Layout
      onClose={() => history.push(PATH_TAX_WITHHOLDINGS)}
      title={`Schedule payment to IRS - ${quarterInfo.quarter} ${quarterInfo.year}`}
      onBack={() => history.goBack()}
      wide
    >
      <WSQueries
        queries={{
          queryWithholdingBalance,
          queryMemberProfile,
          queryActivity,
          queryInternalAccounts
        }}
      >
        {({
          queryWithholdingBalanceData,
          queryMemberProfileData,
          queryInternalAccountsData
        }) => {
          const availableBalance = selectorAccountBalance(
            queryWithholdingBalanceData
          ).available;
          const taxWithholdingAccount = queryInternalAccountsData.data?.find(
            selectorTaxWithholdingAccount
          );
          const ssnLast4 = queryMemberProfileData.profile.ssnLastFourProvided
            ? "last 4"
            : "";

          const dueDateFormatted = toWSDateString(
            quarterInfo.irsDeadline,
            "monthDayYear"
          );

          const isAmountValid = !!(amount && amount <= availableBalance);

          return (
            <WSContainer verticalPadding>
              <WSList gap="XL" mt="M">
                <WSElement>
                  <WSSectionToolbar title="We will submit your payment to the IRS on your behalf." />
                  <WSText.ParagraphSm mt="S" color="gray500">
                    This payment will be scheduled for {quarterInfo.quarter}{" "}
                    {quarterInfo.year}.
                  </WSText.ParagraphSm>
                  <WSText.ParagraphSm mt="S" color="gray500">
                    To ensure timely processing, please submit your payment by{" "}
                    <b>
                      {formatInternalDeadline(quarterInfo.internalDeadline)}
                    </b>
                    .
                  </WSText.ParagraphSm>
                </WSElement>

                <WSCard header="Amount">
                  <WSList gap="XL">
                    <WSInputNumber
                      size="L"
                      value={amount}
                      onChange={setAmount}
                      currency={"USD"}
                      maxFractionDigits={2}
                      max={availableBalance}
                    />

                    <WSInfoBox>
                      <WSText.ParagraphSm color="gray500">
                        Available balance:{" "}
                        {formatMoney(availableBalance, "USD")}
                      </WSText.ParagraphSm>
                    </WSInfoBox>

                    <FromTo
                      fromLabel="From"
                      fromContent={
                        taxWithholdingAccount ? (
                          <LabelMethod
                            type="InternalAccount"
                            id={taxWithholdingAccount.id}
                          />
                        ) : (
                          <WSText.ParagraphSm>
                            Tax Withholdings
                          </WSText.ParagraphSm>
                        )
                      }
                      toLabel="To"
                      toContent={
                        <WSText.ParagraphSm>
                          Scheduled payment to IRS - {quarterInfo.quarter}{" "}
                          {quarterInfo.year} by {dueDateFormatted}
                        </WSText.ParagraphSm>
                      }
                    />

                    <WSAlert
                      theme="info"
                      icon="info-circle"
                      title="Understanding your tax payment"
                    >
                      Your verified SSN (
                      {ssnLast4 ? `---${ssnLast4}` : "on file"}) from your
                      account details will be used for tax payments.
                    </WSAlert>

                    <SchedulePaymentError
                      error={scheduleTaxPaymentMeta.error}
                      internalDeadline={quarterInfo.internalDeadline}
                      quarter={quarterInfo.quarter}
                      year={quarterInfo.year}
                    />

                    <WSActions
                      alignment="fill"
                      buttons={[
                        {
                          kind: "Secondary",
                          label: "Cancel",
                          type: "button",
                          onClick: () => history.goBack()
                        },
                        {
                          label: "Request scheduled payment",
                          disabled: !isAmountValid,
                          loading: scheduleTaxPaymentMeta.isLoading,
                          type: "submit",
                          onAsyncClick: handleSubmit
                        }
                      ]}
                    />
                  </WSList>
                </WSCard>
              </WSList>
            </WSContainer>
          );
        }}
      </WSQueries>
    </Layout>
  );
};
